import { type TFunction } from 'react-i18next';

import { getErrorMessage } from '@wf/sw-ui-kit';

import { ArrayFields, NamesAndPartiesTabFields } from '../types/fieldEnums';
import { type IErrorResponse, LicenseTypeNature } from '../../../types';
import { arrayFieldBracketRegexp } from '../../../constants/regex';
import { FieldPrefix } from '../constants/fieldPrefixes';

import { type additionalFieldsMapByTabSelector } from '../store/selectors';
import { getConsignmentTabFields, getHeaderTabFields } from './tabFields';
import { splitCamelCase } from '../../../helpers';
import { getTabIndexes } from './getTabIndexes';
import i18n from '../../../configs/i18n';

// backend error formats
// "companyCode"
// "additionalFields.af_8573"
// "items[0].companyCode"
// "attachedDocuments[0].companyCode"
// "fees[0].companyCode"
// "beneficiaries[0].companyCode"
// "items[0].additionalFields.af_12464"

enum ErrorPrefix {
  lpco = '',
  item = 'items',
  attachedDocument = 'attachedDocuments',
  beneficiary = 'beneficiaries',
  fee = 'fees',
  additionalFields = 'additionalFields',
}

export function getMandatoryAttachmentError(messageArguments: string[]) {
  if (messageArguments.length === 1) {
    return i18n.t('Attached document {{code}} is mandatory', { code: messageArguments[0] });
  } else {
    const codes = messageArguments.join(', ');

    return i18n.t('Attached documents {{codes}} are mandatory', { codes });
  }
}

export function convertArrayFieldError({
  error,
  entity,
  additionalFields,
  tabIndexes,
}: {
  error: IErrorResponse;
  entity: ArrayFields.items | ArrayFields.attachedDocuments | ArrayFields.beneficiaries | ArrayFields.fees;
  additionalFields?: Record<string, string>;
  tabIndexes: ReturnType<typeof getTabIndexes>;
}) {
  const tabIndexByEntity = {
    [ArrayFields.items]: tabIndexes.ITEMS,
    [ArrayFields.attachedDocuments]: tabIndexes.ATTACHED_DOCUMENTS,
    [ArrayFields.beneficiaries]: tabIndexes.NAMES_AND_PARTIES,
    [ArrayFields.fees]: tabIndexes.FEES,
  };

  const [, index, name] = error.field.split(arrayFieldBracketRegexp);

  let convertedName;

  if (additionalFields && name?.includes(FieldPrefix.additionalField)) {
    const [, fieldName] = name.split('.') as [string, string];
    convertedName = additionalFields[fieldName];
  } else if (name) {
    convertedName = splitCamelCase(name);
  }

  const label =
    typeof index === 'undefined'
      ? i18n.t(splitCamelCase(entity))
      : `${i18n.t(splitCamelCase(entity.slice(0, -1))) as string} ${Number(index) + 1} ${
          i18n.t(convertedName) as string
        }`;

  return {
    ...error,
    tabIndex: tabIndexByEntity[entity],
    entity,
    isFieldError: !!name,
    fieldName: error.field.split(arrayFieldBracketRegexp).join('.'),
    label,
    errorMessage: getErrorMessage(i18n.t as TFunction, error.messageCode, error.messageArguments),
    ...(index && { index: Number(index) }),
  };
}

export default function convertErrors(
  errors: IErrorResponse[],
  additionalFieldsByTab: ReturnType<typeof additionalFieldsMapByTabSelector>,
  licenseTypeNature?: LicenseTypeNature
) {
  const tabIndexes = getTabIndexes(licenseTypeNature);

  let mandatoryAttachments: string[] = [];

  const convertedErrors = errors.map((error) => {
    const { field, messageCode, messageArguments } = error;
    const errorMessage = getErrorMessage(i18n.t as TFunction, messageCode, messageArguments);

    const headerTabFields = getHeaderTabFields(licenseTypeNature);

    if (field in headerTabFields) {
      return {
        ...error,
        tabIndex: tabIndexes.HEADER,
        fieldName: field,
        isFieldError: true,
        label: splitCamelCase(field),
        errorMessage,
      };
    }

    if (field in NamesAndPartiesTabFields) {
      return {
        ...error,
        tabIndex: tabIndexes.NAMES_AND_PARTIES,
        fieldName: field,
        isFieldError: true,
        label: splitCamelCase(field),
        errorMessage,
      };
    }

    if (licenseTypeNature === LicenseTypeNature.EPHYTO) {
      const consignmentTabFields = getConsignmentTabFields();

      if (field in consignmentTabFields) {
        return {
          ...error,
          tabIndex: tabIndexes.CONSIGNMENT,
          fieldName: field,
          isFieldError: true,
          label: splitCamelCase(field),
          errorMessage,
        };
      }
    }

    if (field.startsWith(ErrorPrefix.additionalFields)) {
      const [, fieldName] = field.split('.') as [string, string];
      let tabIndex;
      let label;

      if (fieldName in additionalFieldsByTab.HEADER) {
        tabIndex = tabIndexes.HEADER;
        label = additionalFieldsByTab.HEADER[fieldName];
      } else if (fieldName in additionalFieldsByTab.NAMES_AND_PARTIES) {
        tabIndex = tabIndexes.NAMES_AND_PARTIES;
        label = additionalFieldsByTab.NAMES_AND_PARTIES[fieldName];
      } else if (fieldName in additionalFieldsByTab.CONSIGNMENT) {
        tabIndex = tabIndexes.CONSIGNMENT;
        label = additionalFieldsByTab.CONSIGNMENT[fieldName];
      } else if (fieldName in additionalFieldsByTab.ATTACHED_DOCUMENT) {
        tabIndex = tabIndexes.ATTACHED_DOCUMENTS;
        label = additionalFieldsByTab.ATTACHED_DOCUMENT[fieldName];
      }

      return {
        ...error,
        tabIndex,
        fieldName: field,
        isFieldError: true,
        label,
        errorMessage,
      };
    }

    if (field.startsWith(ErrorPrefix.item)) {
      return convertArrayFieldError({
        error,
        entity: ArrayFields.items,
        additionalFields: additionalFieldsByTab.ITEM,
        tabIndexes,
      });
    }

    // handle case when attachments with specific codes are mandatory
    if (field === ErrorPrefix.attachedDocument && messageCode.includes('mandatory') && messageArguments.length) {
      mandatoryAttachments = messageArguments;

      return {
        ...error,
        tabIndex: tabIndexes.ATTACHED_DOCUMENTS,
        errorMessage: getMandatoryAttachmentError(messageArguments),
        fieldName: ErrorPrefix.attachedDocument,
      };
    }

    if (field.startsWith(ErrorPrefix.attachedDocument)) {
      return convertArrayFieldError({ error, entity: ArrayFields.attachedDocuments, tabIndexes });
    }

    if (field.startsWith(ErrorPrefix.beneficiary)) {
      return convertArrayFieldError({ error, entity: ArrayFields.beneficiaries, tabIndexes });
    }

    if (field.startsWith(ErrorPrefix.fee)) {
      return convertArrayFieldError({ error, entity: ArrayFields.fees, tabIndexes });
    }

    return {
      ...error,
      fieldName: field,
      label: splitCamelCase(field),
      errorMessage,
    };
  });

  return {
    convertedErrors,
    mandatoryAttachments,
  };
}
